import React from 'react';
import Pill from '../interaction/Pill';
import TitleAndMetadataBar from '../interaction/TitleAndMetadataBar';
import PageHeader from '../interaction/PageHeader';
import UserFilters from './UserFilters';
import UserFormCard, { UserFormActions } from './UserFormCard';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
// Row actions use lucide, matching the rest of the ported interaction kit.
import { Mail, Pencil, UserRoundPlus, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';

// lucide ships 24px icons; the action buttons want them at MUI's small size.
const ACTION_ICON = { width: 18, height: 18 };

export default function UserManagerDesktop({
  companies,
  roles,
  filteredUsers,
  filteredUnassignedUsers,
  groupedUsers,
  groupBy,
  setGroupBy,
  GROUP_BY_OPTIONS,
  loading,
  error,
  success,
  tabValue,
  setTabValue,
  searchQuery,
  setSearchQuery,
  statusFilter,
  setStatusFilter,
  STATUS_OPTIONS,
  drafts,
  savingDrafts,
  handleAddDraft,
  handleRemoveDraft,
  handleDraftChange,
  handleEditUserInline,
  handleCloneUser,
  handleSaveDrafts,
  deleteDialog,
  handleDeleteClick,
  handleDeleteConfirm,
  handleDeleteCancel,
  handleSendWelcomeEmail,
  handleToggleUserStatus,
}) {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Users"
        subtitle="Create users, set the company and roles they sign in with, and manage existing access."
        className="ph--page"
        slot={
          <UserFormActions
            drafts={drafts}
            saving={savingDrafts}
            onAdd={handleAddDraft}
            onSave={handleSaveDrafts}
          />
        }
      />

      {/* Editing a row below adds a card to this same form. */}
      <UserFormCard
        drafts={drafts}
        companies={companies}
        roles={roles}
        onRemove={handleRemoveDraft}
        onChange={handleDraftChange}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <UserFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        groupBy={groupBy}
        setGroupBy={setGroupBy}
        groupByOptions={GROUP_BY_OPTIONS}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        statusOptions={STATUS_OPTIONS}
      />

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
            <Tab label={`Users (${filteredUsers.length})`} />
            <Tab label={`Guests (${filteredUnassignedUsers.length})`} icon={<WarningIcon />} iconPosition="start" />
          </Tabs>

          {tabValue === 0 && (
            filteredUsers.length === 0 ? (
              <Box py={4} textAlign="center">
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  {searchQuery || statusFilter !== 'all'
                    ? 'No users match the current filters.'
                    : 'No users found.'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchQuery || statusFilter !== 'all'
                    ? 'Try a different search term, or set Status back to All.'
                    : 'Use the Add User button above to create your first user.'}
                </Typography>
              </Box>
            ) : (
              groupedUsers.map(({ label: groupLabel, users: groupUsers }) => (
                <Paper key={groupLabel ?? '__all'} sx={{ mb: 2.5, overflow: 'hidden' }}>
                  {groupLabel && (
                    <TitleAndMetadataBar
                      leftElements={[
                        <span key="title" className="tmb-title">{groupLabel}</span>,
                        <span key="count" className="tmb-meta">
                          {groupUsers.length} {groupUsers.length === 1 ? 'user' : 'users'}
                        </span>,
                      ]}
                    />
                  )}
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Email</TableCell>
                          <TableCell>Display Name</TableCell>
                          <TableCell>Company</TableCell>
                          <TableCell>Roles</TableCell>
                          <TableCell>Last Login</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {groupUsers.map((user) => (
                          <TableRow key={user.id || user.uid}>
                            <TableCell sx={{ fontWeight: 600 }}>{user.email}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{user.displayName || '-'}</TableCell>
                            <TableCell>{user.company_name || '-'}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {[...(user.roles || [])].sort().map(role => (
                                  <Pill key={role} label={role} variant="light" size="xs" />
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {user.lastSignIn ? new Date(user.lastSignIn).toLocaleString() : 'Never'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={user.status || 'active'}
                                color={user.status === 'active' ? 'success' : user.status === 'pending' ? 'warning' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Box display="flex" gap={0.5} justifyContent="flex-end">
                                <Tooltip title="Send welcome email">
                                  <IconButton size="small" onClick={() => handleSendWelcomeEmail(user)} color="primary">
                                    <Mail {...ACTION_ICON} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit in the form above">
                                  <IconButton size="small" onClick={() => handleEditUserInline(user)}>
                                    <Pencil {...ACTION_ICON} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Clone as a new user">
                                  <IconButton size="small" onClick={() => handleCloneUser(user)}>
                                    <UserRoundPlus {...ACTION_ICON} />
                                  </IconButton>
                                </Tooltip>
                                {/* Kept in the row even when it cannot be used, so every row
                                    has the same actions in the same columns. */}
                                <Tooltip
                                  title={user.status === 'pending'
                                    ? 'Available once they have signed in for the first time'
                                    : user.status === 'active' ? 'Deactivate' : 'Activate'}
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={user.status === 'pending'}
                                      onClick={() => handleToggleUserStatus(user)}
                                      color={user.status === 'active' ? 'success' : 'error'}
                                    >
                                      {user.status === 'active'
                                        ? <ToggleRight {...ACTION_ICON} />
                                        : <ToggleLeft {...ACTION_ICON} />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Delete user">
                                  <IconButton size="small" onClick={() => handleDeleteClick(user)} color="error">
                                    <Trash2 {...ACTION_ICON} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              ))
            )
          )}

          {tabValue === 1 && (
            <Box>
              <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
                <Typography variant="body2">Authenticated users without assigned roles.</Typography>
              </Alert>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Display Name</TableCell>
                      <TableCell>Email Verified</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Last Sign In</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUnassignedUsers.map((user) => (
                      <TableRow key={user.uid} sx={{ '&:hover': { bgcolor: 'warning.lighter' } }}>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.displayName || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={user.emailVerified ? 'Verified' : 'Not Verified'}
                            color={user.emailVerified ? 'success' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell>
                          {user.lastSignIn ? new Date(user.lastSignIn).toLocaleString() : 'Never'}
                        </TableCell>
                        <TableCell>
                          <Chip label={user.reason} color="warning" size="small" icon={<WarningIcon />} />
                        </TableCell>
                        <TableCell align="right">
                          <Box display="flex" gap={0.5} justifyContent="flex-end">
                          <Tooltip title="Complete setup in the form above">
                            <IconButton
                              size="small"
                              onClick={() => handleEditUserInline({
                                uid: user.uid,
                                email: user.email,
                                displayName: user.displayName,
                                ...user.firestoreData
                              })}
                              color="primary"
                            >
                              <Pencil {...ACTION_ICON} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete user">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick({ uid: user.uid, email: user.email })}
                              color="error"
                            >
                              <Trash2 {...ACTION_ICON} />
                            </IconButton>
                          </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredUnassignedUsers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Box py={4}>
                            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                            <Typography variant="body1" color="text.secondary" gutterBottom>
                              {searchQuery ? 'No guests or unassigned users match your search.' : 'No guests or unassigned users found.'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {searchQuery ? 'Try a different search term.' : 'All authenticated users have been assigned roles!'}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: { borderRadius: 2, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)' }
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" component="div">Delete User</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1">
              Are you sure you want to delete <strong>{deleteDialog.userEmail}</strong>?
            </Typography>
          </Box>
          <Alert
            severity="error"
            variant="outlined"
            sx={{ backgroundColor: 'error.lighter', '& .MuiAlert-icon': { color: 'error.main' } }}
          >
            This action cannot be undone. The user will be permanently deleted from the system.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} variant="outlined" sx={{ minWidth: 100 }}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" sx={{ minWidth: 100 }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
